import React from 'react';

import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { SentryErrorBoundary, type SentryErrorBoundaryProps } from '../sentry';

type PageSegmentProps = {
	children: React.ReactNode;
	fallbackComponent?: SentryErrorBoundaryProps['fallbackComponent'];
	name: string;
	teamName?: SentryErrorBoundaryProps['teamName'];
	touchpointId?: SentryErrorBoundaryProps['touchpointId'];
};

const DefaultFallbackComponent = () => null;

const PageSegmentInternal = ({
	name: segmentName,
	children,
	fallbackComponent = DefaultFallbackComponent,
	teamName,
	touchpointId,
}: PageSegmentProps) => {
	const reportError = useUFOReportError();
	const errorName = `PageSegmentError:${segmentName}`;

	return (
		<SentryErrorBoundary
			loggerName={errorName}
			fallbackComponent={fallbackComponent}
			teamName={teamName}
			touchpointId={touchpointId}
			onError={(error) => {
				// using `useUFOReportError` instead of `setInteractionError`
				// because `setInteractionError` will not work due to mismatching activeInteraction
				// https://atlassian.slack.com/archives/C095EAEKV63/p1756710099202429?thread_ts=1756193218.083929&cid=C095EAEKV63
				reportError({
					name: errorName,
					errorMessage: error.message,
					errorStack: error.stack,
				});
			}}
		>
			{children}
		</SentryErrorBoundary>
	);
};

/**
 * @param name - The name of the segment. Used for error handling and UFOSegment name.
 * @param children - The children of the segment.
 * @param fallbackComponent - The component to render when an error occurs.
 * @returns A component that wraps a segment of the page and provides error handling.
 */
export const PageSegment = (props: PageSegmentProps): React.JSX.Element => {
	return (
		<UFOSegment name={props.name}>
			<PageSegmentInternal {...props} />
		</UFOSegment>
	);
};
